import i18n from '@/utils/i18n';
import React from 'react';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { ItemDetailCard } from '@/experiences/sales-portal/domain/items/components/item-detail-card';
import { ItemDetailSectionWrapper } from '@/experiences/sales-portal/domain/items/components/item-detail-section-wrapper';
import { Pressable, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useExpandableText } from '@/experiences/sales-portal/hooks/use-expandable-text';

const COLLAPSED_LINE_LIMIT = 3;
const TOGGLE_HIT_SLOP = 4;

/**
 * Props for {@link ProductDescription}.
 */
export interface ProductDescriptionProps {
  /**
   * Description text. When null / empty the card renders nothing — the
   * Details tab does not show an empty description card.
   */
  description: string | null;
}

/**
 * Product description section for the item-detail Details tab. Bordered card
 * with the description truncated to three lines. A "Show more" / "Show less"
 * toggle is rendered only when the description actually exceeds three lines;
 * overflow detection and the expand/collapse state live in
 * {@link useExpandableText}. Renders `null` when no description is available
 * so the parent tab can compose sections unconditionally.
 *
 * @param props - {@link ProductDescriptionProps}.
 * @returns The rendered card, or `null` when there is no description.
 */
export const ProductDescription: React.FC<ProductDescriptionProps> = ({
  description,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();
  const {
    canExpand,
    isExpanded,
    onCollapsedTextLayout,
    onFullTextHeightLayout,
    onFullTextLineLayout,
    toggle,
  } = useExpandableText(COLLAPSED_LINE_LIMIT);

  if (!description) {
    return null;
  }

  return (
    <ItemDetailSectionWrapper
      title={i18n.t('sales_portal.order_entry.item_detail.description.title')}
    >
      <ItemDetailCard>
        <Typography
          color={theme.sk.color.text.primary}
          numberOfLines={isExpanded ? undefined : COLLAPSED_LINE_LIMIT}
          onLayout={onCollapsedTextLayout}
          variant="paragraph-md"
        >
          {description}
        </Typography>

        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          pointerEvents="none"
        >
          <View style={styles.shadow}>
            <Typography
              onLayout={onFullTextHeightLayout}
              onTextLayout={onFullTextLineLayout}
              variant="paragraph-md"
            >
              {description}
            </Typography>
          </View>
        </View>

        {canExpand ? (
          <Pressable
            accessibilityRole="button"
            hitSlop={TOGGLE_HIT_SLOP}
            onPress={toggle}
            style={styles.toggle}
          >
            <Typography
              color={theme.sk.color.text.blue}
              variant="paragraph-md-600"
            >
              {i18n.t(
                isExpanded
                  ? 'sales_portal.order_entry.item_detail.description.show_less'
                  : 'sales_portal.order_entry.item_detail.description.show_more',
              )}
            </Typography>
          </Pressable>
        ) : null}
      </ItemDetailCard>
    </ItemDetailSectionWrapper>
  );
};

const useStyles = createStyles(() => ({
  shadow: {
    left: 0,
    opacity: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  toggle: {
    alignSelf: 'flex-start',
  },
}));
